import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreditCard,
  Shield,
  Bell,
  Moon,
  LogOut,
  ChevronRight,
  Trophy,
  Pencil,
  X,
  Check,
} from 'lucide-react';
import PageLayout from '../components/layout/PageLayout';
import SkeletonCard from '../components/ui/SkeletonCard';
import { useAuth } from '../contexts/AuthContext';
import { authApi, onboardingApi, statsApi } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import { computeLevel } from '../mocks/level.mock';
import type { GameType, Experience, Plan } from '../types/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const PLAN_BADGE: Record<Plan, { label: string; bg: string; color: string }> = {
  free: { label: 'FREE', bg: '#333', color: '#aaa' },
  pro: { label: 'PRO', bg: '#f5c800', color: '#000' },
  elite: { label: 'ELITE', bg: '#00e676', color: '#000' },
};

const GAME_BADGE: Record<string, { bg: string; color: string }> = {
  MTT: { bg: 'rgba(25,118,210,0.15)', color: '#64b5f6' },
  Cash: { bg: 'rgba(0,230,118,0.15)', color: '#00e676' },
  SNG: { bg: 'rgba(124,77,255,0.15)', color: '#ce93d8' },
  'Spin&Go': { bg: 'rgba(245,124,0,0.12)', color: '#ffb74d' },
  Iniciante: { bg: '#333', color: '#aaa' },
};

function PlanBadge({ plan }: { plan: Plan }) {
  const s = PLAN_BADGE[plan] ?? PLAN_BADGE.free;
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        fontSize: 10,
        fontWeight: 700,
        padding: '3px 8px',
        borderRadius: 5,
        letterSpacing: '0.08em',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {s.label}
    </span>
  );
}

function Toggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        width: 40,
        height: 22,
        borderRadius: 11,
        background: checked ? '#00e676' : '#333',
        border: 'none',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background 0.2s',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 3,
          left: checked ? 21 : 3,
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: '#fff',
          transition: 'left 0.2s',
        }}
      />
    </button>
  );
}

const GAME_TYPES: GameType[] = ['Iniciante', 'MTT', 'Cash', 'SNG'];
const EXPERIENCES: Experience[] = [
  'Iniciante',
  'Intermediário',
  'Avançado',
  'Profissional',
  'Não informado',
];

const card: React.CSSProperties = {
  background: '#1a1a1a',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: 10,
  marginBottom: 16,
};

const labelStyle: React.CSSProperties = {
  color: '#555',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  fontFamily: 'Inter, sans-serif',
  marginBottom: 2,
};

// ─── Modal overlay ─────────────────────────────────────────────────────────────
function Modal({
  onClose,
  children,
}: {
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#1a1a1a',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: 12,
          padding: 28,
          width: '100%',
          maxWidth: 440,
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

// ─── Profile ──────────────────────────────────────────────────────────────────
export default function Profile() {
  const { user: authUser, logout, patchUser } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: userData,
    isLoading: userLoading,
  } = useQuery({
    queryKey: queryKeys.me,
    queryFn: () => authApi.me().then((r) => r.user),
    initialData: authUser ?? undefined,
    staleTime: 300000,
  });

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: queryKeys.stats,
    queryFn: statsApi.overview,
    staleTime: 300000,
  });

  const updateProfile = useMutation({
    mutationFn: (data: Parameters<typeof onboardingApi.save>[0]) =>
      onboardingApi.save(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.me });
      setEditingPrefs(false);
    },
  });

  const user = userData ?? authUser;
  const level = computeLevel(statsData?.sessions_count ?? 0);

  // Local state
  const [editingProfile, setEditingProfile] = useState(false);
  const [editName, setEditName] = useState('');
  const [editingPrefs, setEditingPrefs] = useState(false);
  const [prefForm, setPrefForm] = useState({
    game_type: '',
    stakes: '',
    experience: '',
    goal: '',
    frequency: '',
  });
  const [prefError, setPrefError] = useState<string | null>(null);
  const [notifEnabled, setNotifEnabled] = useState(
    () => localStorage.getItem('pm_notifications') === 'true'
  );

  function openEditPrefs() {
    setPrefForm({
      game_type: user?.game_type ?? '',
      stakes: user?.stakes ?? '',
      experience: user?.experience ?? '',
      goal: user?.goal ?? '',
      frequency: user?.hours_per_week ?? '',
    });
    setPrefError(null);
    setEditingPrefs(true);
  }

  function handleSavePrefs() {
    updateProfile.mutate(prefForm, {
      onError: (err: unknown) =>
        setPrefError(err instanceof Error ? err.message : 'Erro ao salvar'),
    });
  }

  function handleNotifChange(v: boolean) {
    setNotifEnabled(v);
    localStorage.setItem('pm_notifications', String(v));
  }

  const sectionTitle: React.CSSProperties = {
    color: '#fff',
    fontSize: 18,
    fontWeight: 700,
    fontFamily: 'Inter, sans-serif',
    margin: 0,
  };

  const inputStyle: React.CSSProperties = {
    background: '#0d0d0d',
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#fff',
    borderRadius: 7,
    padding: '9px 12px',
    fontSize: 13,
    fontFamily: 'Inter, sans-serif',
    outline: 'none',
    width: '100%',
  };

  return (
    <PageLayout activeRoute="/profile">
      <style>{`@keyframes skeleton-pulse { 0%,100%{opacity:1} 50%{opacity:.45} }`}</style>

      {/* Edit Profile Modal */}
      {editingProfile && (
        <Modal onClose={() => setEditingProfile(false)}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 20,
            }}
          >
            <h3
              style={{
                color: '#fff',
                fontSize: 16,
                fontWeight: 700,
                margin: 0,
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Editar Perfil
            </h3>
            <button
              onClick={() => setEditingProfile(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#555',
                cursor: 'pointer',
              }}
            >
              <X size={18} />
            </button>
          </div>
          <div style={{ marginBottom: 16 }}>
            <p style={labelStyle}>Nome</p>
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => {
                if (editName.trim()) patchUser({ name: editName.trim() });
                setEditingProfile(false);
              }}
              style={{
                flex: 1,
                background: '#00e676',
                color: '#000',
                border: 'none',
                borderRadius: 7,
                padding: '10px',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Salvar
            </button>
            <button
              onClick={() => setEditingProfile(false)}
              style={{
                background: 'none',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#aaa',
                borderRadius: 7,
                padding: '10px 18px',
                fontSize: 13,
                cursor: 'pointer',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Cancelar
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Prefs Modal */}
      {editingPrefs && (
        <Modal onClose={() => setEditingPrefs(false)}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 20,
            }}
          >
            <h3
              style={{
                color: '#fff',
                fontSize: 16,
                fontWeight: 700,
                margin: 0,
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Editar Preferências
            </h3>
            <button
              onClick={() => setEditingPrefs(false)}
              style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer' }}
            >
              <X size={18} />
            </button>
          </div>

          {prefError && (
            <div
              style={{
                background: 'rgba(229,57,53,0.1)',
                border: '1px solid rgba(229,57,53,0.3)',
                borderRadius: 7,
                padding: '9px 12px',
                color: '#e53935',
                fontSize: 12,
                marginBottom: 14,
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {prefError}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Game type */}
            <div>
              <p style={labelStyle}>Modalidade</p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {GAME_TYPES.map((gt) => {
                  const s = GAME_BADGE[gt] ?? GAME_BADGE.Iniciante;
                  const sel = prefForm.game_type === gt;
                  return (
                    <button
                      key={gt}
                      onClick={() => setPrefForm((p) => ({ ...p, game_type: gt }))}
                      style={{
                        background: sel ? s.bg : 'rgba(255,255,255,0.04)',
                        color: sel ? s.color : '#666',
                        border: sel ? `1px solid ${s.color}` : '1px solid rgba(255,255,255,0.07)',
                        borderRadius: 6,
                        padding: '6px 14px',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      {gt}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p style={labelStyle}>Stakes</p>
              <input
                value={prefForm.stakes}
                onChange={(e) => setPrefForm((p) => ({ ...p, stakes: e.target.value }))}
                placeholder="ex: 5/10, 10/20"
                style={inputStyle}
              />
            </div>

            <div>
              <p style={labelStyle}>Experiência</p>
              <select
                value={prefForm.experience}
                onChange={(e) =>
                  setPrefForm((p) => ({ ...p, experience: e.target.value }))
                }
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                {EXPERIENCES.map((e) => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <p style={labelStyle}>Objetivo</p>
              <textarea
                value={prefForm.goal}
                onChange={(e) => setPrefForm((p) => ({ ...p, goal: e.target.value }))}
                rows={3}
                style={{ ...inputStyle, resize: 'none' }}
              />
            </div>

            <div>
              <p style={labelStyle}>Tempo / semana</p>
              <select
                value={prefForm.frequency}
                onChange={(e) =>
                  setPrefForm((p) => ({ ...p, frequency: e.target.value }))
                }
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                {['< 3h/semana', '3–10h/semana', '10–20h/semana', '20+ h/semana'].map(
                  (o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  )
                )}
              </select>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleSavePrefs}
                disabled={updateProfile.isPending}
                style={{
                  flex: 1,
                  background: '#00e676',
                  color: '#000',
                  border: 'none',
                  borderRadius: 7,
                  padding: '11px',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: updateProfile.isPending ? 'not-allowed' : 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  opacity: updateProfile.isPending ? 0.7 : 1,
                }}
              >
                {updateProfile.isPending ? 'Salvando…' : 'Salvar'}
              </button>
              <button
                onClick={() => setEditingPrefs(false)}
                style={{
                  background: 'none',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#aaa',
                  borderRadius: 7,
                  padding: '11px 18px',
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Header ── */}
      <div style={{ marginBottom: 28 }}>
        <h1
          style={{
            color: '#fff',
            fontSize: 24,
            fontWeight: 700,
            margin: 0,
            fontFamily: 'Inter, sans-serif',
          }}
        >
          Meu Perfil
        </h1>
        <p
          style={{
            color: '#555',
            fontSize: 13,
            marginTop: 4,
            fontFamily: 'Inter, sans-serif',
          }}
        >
          Gerencie suas preferências e configurações
        </p>
      </div>

      {/* ── Identity Card ── */}
      <div style={{ ...card, padding: '28px' }}>
        {userLoading ? (
          <div style={{ display: 'flex', gap: 20 }}>
            <SkeletonCard height={72} w={72} />
            <div style={{ flex: 1 }}>
              <SkeletonCard height={20} style={{ marginBottom: 10 }} />
              <SkeletonCard height={14} w="70%" />
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
            {/* Avatar */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <svg viewBox="0 0 80 80" width={80} height={80}>
                <circle cx="40" cy="40" r="36" fill="#2a2a2a" />
                <circle
                  cx="40"
                  cy="40"
                  r="36"
                  fill="none"
                  stroke="#00e676"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 36}`}
                  strokeDashoffset={`${
                    2 * Math.PI * 36 * (1 - level.xpCurrent / level.xpRequired)
                  }`}
                  transform="rotate(-90 40 40)"
                />
                <text
                  x="40"
                  y="47"
                  textAnchor="middle"
                  fill="#fff"
                  fontSize="26"
                  fontWeight="700"
                  fontFamily="Inter, sans-serif"
                >
                  {user?.name?.charAt(0)?.toUpperCase() ?? '?'}
                </text>
              </svg>
              {/* Edit badge */}
              <button
                onClick={() => {
                  setEditName(user?.name ?? '');
                  setEditingProfile(true);
                }}
                style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: '#00e676',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Pencil size={11} color="#000" />
              </button>
            </div>

            {/* Info */}
            <div style={{ flex: 1 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 4,
                  flexWrap: 'wrap',
                }}
              >
                <h2
                  style={{
                    color: '#fff',
                    fontSize: 22,
                    fontWeight: 700,
                    margin: 0,
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {user?.name ?? '—'}
                </h2>
                {user?.plan && <PlanBadge plan={user.plan} />}
              </div>
              <p
                style={{
                  color: '#555',
                  fontSize: 13,
                  margin: '0 0 4px',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                {user?.email}
              </p>
              <p
                style={{
                  color: '#444',
                  fontSize: 12,
                  margin: '0 0 16px',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                Membro desde{' '}
                {localStorage.getItem('pm_member_since') ?? 'Março 2024'}
              </p>

              {/* Inline stats */}
              <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
                {[
                  {
                    val: statsData?.sessions_count ?? '—',
                    label: 'Sessões',
                    color: '#00e676',
                  },
                  {
                    val: statsData
                      ? Math.round(
                          statsData.avg_hands * statsData.sessions_count
                        ).toLocaleString()
                      : '—',
                    label: 'Mãos Analisadas',
                    color: '#f5c800',
                  },
                  { val: level.level, label: 'Nível', color: '#fff' },
                ].map((s) => (
                  <div key={s.label}>
                    <p
                      style={{
                        color: s.color,
                        fontSize: 20,
                        fontWeight: 700,
                        margin: 0,
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      {statsLoading ? '—' : s.val}
                    </p>
                    <p
                      style={{
                        color: '#555',
                        fontSize: 11,
                        margin: '2px 0 0',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>

              <button
                onClick={() => {
                  setEditName(user?.name ?? '');
                  setEditingProfile(true);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: 'none',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: 'rgba(255,255,255,0.7)',
                  borderRadius: 7,
                  padding: '7px 16px',
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                <Pencil size={12} />
                Editar Perfil
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Level Progress ── */}
      <div style={{ ...card, padding: '24px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 14,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Trophy size={16} color="#f5c800" />
            <h3
              style={{
                color: '#fff',
                fontSize: 15,
                fontWeight: 600,
                margin: 0,
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Progresso de Nível
            </h3>
          </div>
          <span
            style={{
              color: '#00e676',
              fontSize: 13,
              fontWeight: 700,
              fontFamily: 'Inter, sans-serif',
            }}
          >
            {level.xpCurrent} / {level.xpRequired} XP
          </span>
        </div>
        <div
          style={{
            height: 8,
            background: '#2a2a2a',
            borderRadius: 4,
            marginBottom: 10,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${(level.xpCurrent / level.xpRequired) * 100}%`,
              background: '#00e676',
              borderRadius: 4,
              transition: 'width 0.6s ease',
            }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {['Iniciante', 'Intermediário', 'Avançado'].map((lbl) => (
            <span
              key={lbl}
              style={{
                fontSize: 12,
                color: lbl === level.label ? '#00e676' : '#444',
                fontWeight: lbl === level.label ? 700 : 400,
                fontFamily: 'Inter, sans-serif',
                position: 'relative',
              }}
            >
              {lbl}
            </span>
          ))}
        </div>
      </div>

      {/* ── Bottom row ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
        }}
      >
        {/* Game Preferences */}
        <div style={{ ...card, padding: '24px', margin: 0 }}>
          <p
            style={{
              ...labelStyle,
              color: '#555',
              marginBottom: 8,
            }}
          >
            PERFIL DE JOGO
          </p>
          <h3 style={{ ...sectionTitle, marginBottom: 20 }}>
            Preferências de Jogo
          </h3>

          {userLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[1, 2, 3, 4, 5].map((i) => <SkeletonCard key={i} height={42} />)}
            </div>
          ) : (
            <>
              {[
                {
                  label: 'MODALIDADE',
                  value: (
                    <span
                      style={{
                        background:
                          GAME_BADGE[user?.game_type ?? 'Iniciante']?.bg ??
                          '#333',
                        color:
                          GAME_BADGE[user?.game_type ?? 'Iniciante']?.color ??
                          '#aaa',
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '3px 10px',
                        borderRadius: 6,
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      {user?.game_type ?? '—'}
                    </span>
                  ),
                },
                { label: 'STAKES', value: user?.stakes ?? '—' },
                { label: 'EXPERIÊNCIA', value: user?.experience ?? '—' },
                { label: 'OBJETIVO', value: user?.goal ?? '—' },
                { label: 'TEMPO/SEMANA', value: user?.hours_per_week ?? '—' },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    padding: '14px 0',
                  }}
                >
                  <p style={labelStyle}>{label}</p>
                  {typeof value === 'string' ? (
                    <span
                      style={{
                        color: 'rgba(255,255,255,0.75)',
                        fontSize: 13,
                        fontFamily: 'Inter, sans-serif',
                        textAlign: 'right',
                        maxWidth: '55%',
                      }}
                    >
                      {value}
                    </span>
                  ) : (
                    value
                  )}
                </div>
              ))}

              <button
                onClick={openEditPrefs}
                style={{
                  width: '100%',
                  background: '#00e676',
                  color: '#000',
                  border: 'none',
                  borderRadius: 8,
                  padding: '13px',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  marginTop: 20,
                }}
              >
                Atualizar meu perfil
              </button>
            </>
          )}
        </div>

        {/* Account & Access */}
        <div style={{ ...card, padding: '24px', margin: 0 }}>
          <p style={{ ...labelStyle, color: '#555', marginBottom: 8 }}>
            CONFIGURAÇÕES
          </p>
          <h3 style={{ ...sectionTitle, marginBottom: 20 }}>
            Conta e Acessos
          </h3>

          {[
            {
              icon: <CreditCard size={16} />,
              label: 'Gerenciar assinatura',
              sub: `Plano ${user?.plan?.toUpperCase() ?? 'FREE'} – Ativo`,
              onClick: () =>
                window.open('https://pay.kiwify.com.br/Ad3EqAb', '_blank'),
              right: <ChevronRight size={14} color="#444" />,
            },
            {
              icon: <Shield size={16} />,
              label: 'Segurança',
              sub: 'Senha e autenticação',
              onClick: () =>
                user?.email &&
                authApi
                  .forgotPassword(user.email)
                  .then(() => alert('Email de redefinição enviado!')),
              right: <ChevronRight size={14} color="#444" />,
            },
          ].map(({ icon, label, sub, onClick, right }) => (
            <button
              key={label}
              onClick={onClick}
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: 12,
                padding: '16px 0',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                background: 'none',
                border2: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                borderLeft: 'none',
                borderRight: 'none',
                borderTop: 'none',
                borderBottom2: '1px solid rgba(255,255,255,0.05)',
              } as React.CSSProperties}
            >
              <span style={{ color: '#555', display: 'flex' }}>{icon}</span>
              <div style={{ flex: 1 }}>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.8)',
                    fontSize: 13,
                    margin: 0,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  {label}
                </p>
                <p
                  style={{
                    color: '#555',
                    fontSize: 11,
                    margin: '2px 0 0',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  {sub}
                </p>
              </div>
              {right}
            </button>
          ))}

          {/* Notifications toggle */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '16px 0',
              borderBottom: '1px solid rgba(255,255,255,0.05)',
            }}
          >
            <span style={{ color: '#555', display: 'flex' }}>
              <Bell size={16} />
            </span>
            <div style={{ flex: 1 }}>
              <p
                style={{
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: 13,
                  margin: 0,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                }}
              >
                Notificações
              </p>
              <p
                style={{
                  color: '#555',
                  fontSize: 11,
                  margin: '2px 0 0',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                Email e push
              </p>
            </div>
            <Toggle checked={notifEnabled} onChange={handleNotifChange} />
          </div>

          {/* Dark mode */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '16px 0',
              borderBottom: '1px solid rgba(255,255,255,0.05)',
            }}
          >
            <span style={{ color: '#555', display: 'flex' }}>
              <Moon size={16} />
            </span>
            <div style={{ flex: 1 }}>
              <p
                style={{
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: 13,
                  margin: 0,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                }}
              >
                Aparência
              </p>
              <p
                style={{
                  color: '#555',
                  fontSize: 11,
                  margin: '2px 0 0',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                Modo escuro ativado
              </p>
            </div>
            <ChevronRight size={14} color="#444" />
          </div>

          {/* Logout */}
          <button
            onClick={logout}
            style={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              gap: 12,
              padding: '16px 0 0',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background =
                'rgba(229,57,53,0.06)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'none';
            }}
          >
            <LogOut size={16} color="#e53935" />
            <p
              style={{
                color: '#e53935',
                fontSize: 13,
                margin: 0,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
              }}
            >
              Sair da conta
            </p>
          </button>
        </div>
      </div>
    </PageLayout>
  );
}
